import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useContext,
  useEffect,
} from "react";
import validator from "validator";
import {
  SimpleDialog,
  Button,
  SimpleForm,
  SimpleUtility,
} from "simple-ui-components";

import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";

import { post, put } from "../../server-requests/serverRequests";
import { AuthContext } from "../authContextProvider";
import { Maps } from "../shared/map";
import { IconsListDetail } from "../shared/iconDetail";
import { IconListDetailStyled } from "../../theme";
import { getTranslation } from "../languageProvider";
import { Log, ReplaceWord } from "../shared/utility";

const validateEquipment = ({
  serialNumber,
  stateId,
  icon,
  observations,
  address,
}) => {
  let error = "";
  if (!serialNumber) {
    error = {
      message:
        getTranslation("0113") &&
        ReplaceWord(getTranslation("0113"), "**NAME**", getTranslation("0102")),
      errorOn: "number",
    };
  } else {
    if (!validator.isLength(serialNumber, { min: 1, max: 30 })) {
      error = {
        message:
          getTranslation("0119") &&
          ReplaceWord(
            getTranslation("0119"),
            "**NAME**",
            getTranslation("0102"),
            "**MIN**",
            1,
            "**MAX**",
            30
          ),
        errorOn: "number",
      };
    } else {
      if (!validator.isLength(observations, { min: 1, max: 200 })) {
        error = {
          message:
            getTranslation("0119") &&
            ReplaceWord(
              getTranslation("0119"),
              "**NAME**",
              getTranslation("0016"),
              "**MIN**",
              "0",
              "**MAX**",
              200
            ),
          errorOn: "observation",
        };
      } else {
        if (!stateId) {
          error = {
            message:
              getTranslation("0113") &&
              ReplaceWord(
                getTranslation("0113"),
                "**NAME**",
                getTranslation("0109")
              ),
            errorOn: "state",
          };
        } else {
          if (!icon) {
            error = {
              message:
                getTranslation("0113") &&
                ReplaceWord(
                  getTranslation("0113"),
                  "**NAME**",
                  getTranslation("0114")
                ),
              errorOn: "icon",
            };
          } else {
            if (!validator.isLength(address, { min: 0, max: 200 })) {
              error = {
                message:
                  getTranslation("0119") &&
                  ReplaceWord(
                    getTranslation("0119"),
                    "**NAME**",
                    getTranslation("0110"),
                    "**MIN**",
                    "0",
                    "**MAX**",
                    200
                  ),
              };
            }
          }
        }
      }
    }
  }
  return error;
};

export const AddEditEquipment = forwardRef(
  ({ UpdateSuccess, clientList, stateList, iconList }, ref) => {
    const { Dialog, DialogBody, DialogFooter } = SimpleDialog;
    const { InputGroup } = SimpleForm;
    const { GetIndexBy } = SimpleUtility;

    const { snackbarHandler } = useContext(AuthContext);

    const [open, setOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [marker, setMarker] = useState([]);
    const [toEdit, setToEdit] = useState({});
    const [serialNumber, setSerialNumber] = useState("");
    const [selectedClient, setSelectedClient] = useState([]);
    const [currentModel, setCurrentModel] = useState({});
    const [pos, setPos] = useState({});
    const [getError, setError] = useState({
      number: false,
      observation: false,
      state: false,
      icon: false,
    });
    const [observation, setObservation] = useState("");
    const [address, setAddress] = useState("");
    const [selectedState, setSelectedState] = useState([]);
    const [mapReload, setMapReload] = useState(true);
    const [selectedIcon, setSelectedIcon] = useState(null);

    const handleClose = () => setOpen(false);

    // trigger from props
    useImperativeHandle(ref, () => ({
      AddEditEquipmentHandler(type, modelOrEquipment) {
        setOpen(true);
        setError({ number: false, state: false, icon: false });
        if (type === "edit") {
          // modelOrEquipment = Equipment
          setIsEdit(true);
          setCurrentModel(modelOrEquipment.model);
          setToEdit(modelOrEquipment);
          setSerialNumber(modelOrEquipment.serialNumber);
          setObservation(modelOrEquipment.observations);
          setAddress(modelOrEquipment.address);
          setMarker([]);
          setPos({
            lat: Number(modelOrEquipment.latitude),
            lng: Number(modelOrEquipment.longitude),
          });

          let currentState = [];
          if (stateList.length > 0)
            currentState = stateList.filter(
              (state) => state.id === modelOrEquipment.state.id
            );
          setSelectedState(currentState);

          let currentClient = [];
          if (clientList.length > 0)
            currentClient = clientList.filter(
              (client) => client.id === modelOrEquipment.client.id
            );
          setSelectedClient(currentClient);

          let currentIcon = [];
          if (iconList.length > 0)
            currentIcon = iconList.filter(
              (icon) => icon.url === modelOrEquipment.icon
            );
          setSelectedIcon(currentIcon[0]);
        } else {
          setCurrentModel(modelOrEquipment);
          setIsEdit(false);
          setToEdit({});
          setMarker([]);
          setSerialNumber("");
          setObservation("");
          setPos({
            lat: 39.768372,
            lng: -8.185234,
          });
          setSelectedState([]);
          setSelectedClient([]);
          setSelectedIcon(null);
        }
      },
    }));

    const clickAddEditHandler = () => {
      let toSend = {
        serialNumber: serialNumber,
        clientId: selectedClient.length > 0 ? selectedClient[0].id : null,
        stateId: selectedState.length > 0 ? selectedState[0].id : null,
        icon: selectedIcon ? selectedIcon.file : null,
        latitude: pos.lat,
        longitude: pos.lng,
        modelId: currentModel.id,
        observations: observation ? observation : "",
        address: address ? address : "",
      };
      let error = validateEquipment(toSend);
      if (error !== "") {
        snackbarHandler(error.message);
        setError({
          [error.errorOn]: true,
        });
      } else {
        if (isEdit) {
          // edit equipment
          put("equipments", toSend, {
            id: toEdit.id,
          })
            .then(() => {
              snackbarHandler(
                `${
                  getTranslation("0115") &&
                  ReplaceWord(
                    getTranslation("0115"),
                    "**NAME**",
                    getTranslation("0071")
                  )
                }`,
                "success"
              );
              setOpen(false);
              UpdateSuccess();
            })
            .catch((status) => {
              snackbarHandler(getTranslation(`error${status}`));
              Log("Error: update equipments" + status);
            });
        } else {
          // create equipment
          post("equipments", toSend)
            .then(() => {
              snackbarHandler(
                `${
                  getTranslation("0116") &&
                  ReplaceWord(
                    getTranslation("0116"),
                    "**NAME**",
                    getTranslation("0071")
                  )
                }`,
                "success"
              );
              setOpen(false);
              UpdateSuccess();
            })
            .catch((status) => {
              snackbarHandler(getTranslation(`error${status}`));
              Log("Error: create equipments" + status);
            });
        }
      }
    };

    const serialNumberChangeHandler = (event) =>
      setSerialNumber(event.target.value);

    const dragEndHandler = (data) => {
      setAddress(data.address);
      setPos({
        lat: Number(data.lat),
        lng: Number(data.lng),
      });
    };

    const clientChangeHandler = (client) => setSelectedClient(client);
    const stateChangeHandler = (state) => setSelectedState(state);
    const onchangeIconHandler = (detail) => setSelectedIcon(detail);

    // set Marker
    useEffect(() => {
      let newMarker = marker;
      newMarker[0] = { pos: pos };
      setMarker(newMarker);
    }, [pos, marker]);

    const myLocationClickHandler = (pos) => {
      let newMarker = marker;
      let index = GetIndexBy(marker, 10, "id");
      if (index === -1) {
        setMarker([...newMarker, { id: 10, pos: pos }]);
      } else {
        newMarker[index] = { id: 10, pos: pos };
        setMarker(newMarker);
      }
      setMapReload((prev) => !prev);
    };

    const observationOnchangeHandler = (event) =>
      setObservation(event.target.value);

    return (
      <>
        {open && (
          <Dialog
            theme="default"
            size="md"
            close={handleClose}
            open={open}
            title={
              isEdit
                ? `${getTranslation("0070")} ${
                    getTranslation("0071") &&
                    getTranslation("0071").toLowerCase()
                  }`
                : `${getTranslation("0087")} ${
                    getTranslation("0071") &&
                    getTranslation("0071").toLowerCase()
                  }`
            }
          >
            <DialogBody className="outline md">
              <InputGroup
                type="input"
                placeholder={getTranslation("0102")}
                className={getError.number ? "error" : ""}
                inputElement={{
                  name: "serialNumber",
                  onChange: serialNumberChangeHandler,
                  type: "text",
                  defaultValue: serialNumber,
                  className: serialNumber ? "focused" : "",
                  translate: "0102",
                }}
              />

              <InputGroup
                type="input"
                placeholder={getTranslation("0016")}
                className={getError.observation ? "error" : ""}
                inputElement={{
                  name: "observation",
                  onChange: observationOnchangeHandler,
                  type: "text",
                  defaultValue: observation,
                  className: observation ? "focused" : "",
                  translate: "0016",
                }}
              />

              {stateList.length > 0 && (
                <InputGroup
                  type="autocomplete"
                  placeholder={getTranslation("0106")}
                  className={getError.state ? "error" : ""}
                  inputElement={{
                    id: "stateListEdit",
                    list: stateList,
                    change: stateChangeHandler,
                    value: selectedState,
                    className: selectedState.length > 0 ? "focused" : "",
                    placeholder: getTranslation("0106"),
                    inputPlaceholder: getTranslation("0050"),
                    emptyText: ReplaceWord(
                      getTranslation("0048"),
                      "**LIST**",
                      getTranslation("0106").toLowerCase()
                    ),
                    multiple: false,
                    clear: true,
                    iconText: true,
                    selectedCallback: true,
                  }}
                />
              )}

              {clientList.length > 0 && (
                <InputGroup
                  type="autocomplete"
                  placeholder={getTranslation("0105")}
                  inputElement={{
                    id: "clientListEdit",
                    list: clientList,
                    change: clientChangeHandler,
                    value: selectedClient,
                    className: selectedClient.length > 0 ? "focused" : "",
                    placeholder: getTranslation("0072"),
                    inputPlaceholder: `${getTranslation(
                      "0125"
                    )}/${getTranslation("0052")}`,
                    emptyText: ReplaceWord(
                      getTranslation("0048"),
                      "**LIST**",
                      getTranslation("0072").toLowerCase()
                    ),
                    multiple: false,
                    clear: true,
                    secondaryOption: "companyName",
                    selectedCallback: true,
                  }}
                />
              )}

              <IconListDetailStyled>
                <b data-translate="0117">{getTranslation("0117")}</b>
                <div className="wrapper">
                  {iconList.length > 0 &&
                    iconList.map((icon, index) => (
                      <IconsListDetail
                        key={index}
                        detail={icon}
                        onChangeIcon={onchangeIconHandler}
                        selectedIcon={selectedIcon}
                      />
                    ))}
                </div>
              </IconListDetailStyled>
              <div style={{ paddingTop: 10 }}>
                <b data-translate="0118">{getTranslation("0118")}</b>
                <Maps
                  list={marker}
                  draggable
                  DragEnd={dragEndHandler}
                  mapHeight={"348px"}
                  MyLocationHandler={myLocationClickHandler}
                  mapReload={mapReload}
                />
              </div>
            </DialogBody>
            <DialogFooter multiple={true}>
              <Button
                type="button"
                clicked={clickAddEditHandler}
                icon={isEdit ? <SaveOutlinedIcon /> : <AddOutlinedIcon />}
                text={isEdit ? getTranslation("0024") : getTranslation("0087")}
                className={isEdit ? "btn-primary" : "btn-secondary"}
                theme="default"
                style={{ marginLeft: "auto" }}
              />
            </DialogFooter>
          </Dialog>
        )}
      </>
    );
  }
);
